import React, { useState, useEffect } from "react";
import { Minimize2, Maximize, Expand, StretchHorizontal } from "lucide-react";
import "./ArticleWidthControls.css";

const WIDTH_PRESETS = {
  narrow: { label: "Narrow", icon: Minimize2, width: "42rem", key: "narrow" },
  comfort: { label: "Comfort", icon: StretchHorizontal, width: "56rem", key: "comfort" },
  wide: { label: "Wide", icon: Expand, width: "72rem", key: "wide" },
  full: { label: "Full", icon: Maximize, width: "min(100%, 96rem)", key: "full" },
};

const STORAGE_KEY = "article-width-preference";

export function useArticleWidth() {
  const [width, setWidth] = useState(() => {
    if (typeof window === "undefined") return "comfort";
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && WIDTH_PRESETS[saved] ? saved : "comfort";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, width);
  }, [width]);

  const widthValue = WIDTH_PRESETS[width]?.width || WIDTH_PRESETS.comfort.width;

  return { width, setWidth, widthValue, presets: WIDTH_PRESETS };
}

export default function ArticleWidthControls({ currentWidth, onChange, position = "top-right" }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Keyboard shortcuts: [ narrower, ] wider
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "[" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        const keys = Object.keys(WIDTH_PRESETS);
        const currentIndex = keys.indexOf(currentWidth);
        const nextIndex = Math.max(0, currentIndex - 1);
        onChange(keys[nextIndex]);
      } else if (e.key === "]" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        const keys = Object.keys(WIDTH_PRESETS);
        const currentIndex = keys.indexOf(currentWidth);
        const nextIndex = Math.min(keys.length - 1, currentIndex + 1);
        onChange(keys[nextIndex]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentWidth, onChange]);

  // Auto-hide after inactivity
  useEffect(() => {
    if (!isExpanded) {
      const timer = setTimeout(() => setIsVisible(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);


  const currentPreset = WIDTH_PRESETS[currentWidth];
  const CurrentIcon = currentPreset?.icon || StretchHorizontal;

  return (
    <>
      {/* Hover zone to show controls when hidden */}
      {!isVisible && !isExpanded && (
        <div
          className={`article-width-hover-zone article-width-hover-zone--${position}`}
          onMouseEnter={() => setIsVisible(true)}
          onClick={() => setIsVisible(true)}
        />
      )}

      <div
        className={`article-width-controls article-width-controls--${position} ${isExpanded ? "is-expanded" : ""} ${isVisible ? "is-visible" : ""}`}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => {
          if (!isExpanded) setIsVisible(false);
        }}
      >
        {/* Main toggle button */}
        <button
          className="article-width-toggle liquid-glass"
          onClick={() => setIsExpanded(!isExpanded)}
          title={`Width: ${currentPreset?.label || "Comfort"} (Ctrl+[ or Ctrl+])`}
          aria-label={`Current width: ${currentPreset?.label}. Click to expand options`}
          aria-expanded={isExpanded}
        >
          <CurrentIcon size={16} />
          <span className="article-width-label">{currentPreset?.label}</span>
        </button>

        {/* Expanded preset buttons */}
        {isExpanded && (
          <div className="article-width-presets liquid-glass">
            {Object.entries(WIDTH_PRESETS).map(([key, preset]) => {
              const Icon = preset.icon;
              const isActive = currentWidth === key;
              return (
                <button
                  key={key}
                  className={`article-width-preset ${isActive ? "is-active" : ""}`}
                  onClick={() => {
                    onChange(key);
                    setIsExpanded(false);
                  }}
                  title={preset.label}
                  aria-label={`Set width to ${preset.label}`}
                  aria-pressed={isActive}
                >
                  <Icon size={14} />
                  <span>{preset.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
